import { Trash2 } from 'lucide-react';
import { Button } from '../ui';
import type { AcousticCalibrationSnapshot, AcousticCalibrationTag } from '../../lib/acoustic/calibrationTypes';
import { ACOUSTIC_CALIBRATION_TAGS } from '../../lib/acoustic/calibrationTypes';

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch {
    return iso;
  }
}

function autoLabel(detected: string): string {
  switch (detected) {
    case 'single_speaker':
      return 'Einzelsprecher';
    case 'conversation':
      return 'Dialog';
    case 'babble':
      return 'Gemurmel';
    case 'shouting':
      return 'Geschrei';
    case 'silence':
      return 'Stille';
    default:
      return 'Unklar';
  }
}

type AcousticCalibrationSnapshotListProps = {
  snapshots: AcousticCalibrationSnapshot[];
  filterTag: AcousticCalibrationTag | 'all';
  selectedId: string | null;
  onFilterTag: (tag: AcousticCalibrationTag | 'all') => void;
  onSelect: (id: string | null) => void;
  onDelete: (id: string) => void;
};

export default function AcousticCalibrationSnapshotList({
  snapshots,
  filterTag,
  selectedId,
  onFilterTag,
  onSelect,
  onDelete
}: AcousticCalibrationSnapshotListProps) {
  const filtered =
    filterTag === 'all' ? snapshots : snapshots.filter((entry) => entry.tag === filterTag);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={filterTag === 'all' ? 'primary' : 'outline'}
          onClick={() => onFilterTag('all')}
        >
          Alle ({snapshots.length})
        </Button>
        {ACOUSTIC_CALIBRATION_TAGS.map((tag) => {
          const count = snapshots.filter((s) => s.tag === tag.key).length;
          if (!count) return null;
          return (
            <Button
              key={tag.key}
              size="sm"
              variant={filterTag === tag.key ? 'primary' : 'outline'}
              onClick={() => onFilterTag(tag.key)}
            >
              {tag.label} ({count})
            </Button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Noch keine Snapshots. Mikrofon starten und eine Situation taggen.
        </p>
      ) : (
        <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {filtered.map((snapshot) => {
            const selected = snapshot.id === selectedId;
            return (
              <li key={snapshot.id}>
                <button
                  type="button"
                  onClick={() => onSelect(selected ? null : snapshot.id)}
                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition ${
                    selected
                      ? 'border-sky-500 bg-sky-50 dark:border-sky-600 dark:bg-sky-950/40'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span>
                      <span className="font-semibold">{snapshot.tagLabel}</span>
                      <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                        {formatTime(snapshot.taggedAt)} · Auto: {autoLabel(snapshot.autoDetected)} (
                        {Math.round(snapshot.autoConfidence * 100)}%)
                        {snapshot.source === 'file' && snapshot.sourceFileName
                          ? ` · Datei: ${snapshot.sourceFileName}`
                          : snapshot.source === 'live'
                          ? ' · Live'
                          : ''}
                      </span>
                      {snapshot.note ? (
                        <span className="mt-1 block text-xs italic text-gray-600 dark:text-gray-300">
                          {snapshot.note}
                        </span>
                      ) : null}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label="Snapshot löschen"
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(snapshot.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
